import { useState, useCallback, useEffect } from "react";
const RAILWAY_URL = "https://web-production-c0e77.up.railway.app";

const C = {
  home: "#16a34a", draw: "#d97706", away: "#2563eb",
  bg: "#f9fafb", card: "#ffffff", border: "#e5e7eb",
  muted: "#9ca3af", text: "#111827", sub: "#6b7280",
  accent: "#111827",
};

// ── Seçenek setleri ──────────────────────────────────────────────────────────
const METRICS = [
  { value: "draw",          label: "🤝 Beraberlik Sayısı" },
  { value: "win",           label: "🏆 Galibiyet Sayısı" },
  { value: "lose",          label: "💔 Mağlubiyet Sayısı" },
  { value: "goals_for",     label: "⚽ Gol Atma" },
  { value: "goals_against", label: "🥅 Gol Yeme" },
  { value: "avg_goals",     label: "📊 Gol Ortalaması" },
  { value: "zero_zero",     label: "0️⃣  0-0 Maç Sayısı" },
  { value: "scores",        label: "📋 Skor Dağılımı" },
];

const LOCATIONS = [
  { value: "all",  label: "🌐 Genel (Ev + Deplasman)" },
  { value: "home", label: "🏠 Sadece İç Saha" },
  { value: "away", label: "✈️  Sadece Deplasman" },
];

const TIME_TYPES = [
  { value: "last_n",      label: "Son N Maç" },
  { value: "this_season", label: "Bu Sezon (2025-26)" },
  { value: "last_season", label: "Geçen Sezon (2024-25)" },
  { value: "all",         label: "Tüm Veri" },
];

const ORDERS = [
  { value: "desc", label: "En Çok → En Az ↓" },
  { value: "asc",  label: "En Az → En Çok ↑" },
];

// ── Küçük bileşenler ─────────────────────────────────────────────────────────
function Select({ label, value, onChange, options }) {
  return (
    <div style={{ flex: 1, minWidth: 160 }}>
      <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1,
        textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        width: "100%", padding: "9px 12px", borderRadius: 6,
        border: `1px solid ${C.border}`, fontSize: 13,
        background: C.card, color: C.text, cursor: "pointer",
      }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function NumInput({ label, value, onChange, min, max, unit = "" }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1,
        textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="range" min={min} max={max} value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{ flex: 1, accentColor: C.accent }}
        />
        <input
          type="number" min={min} max={max} value={value}
          onChange={e => {
            const v = Math.min(max, Math.max(min, Number(e.target.value)));
            onChange(v);
          }}
          style={{
            width: 60, padding: "6px 8px", borderRadius: 6, textAlign: "center",
            border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700,
          }}
        />
        {unit && <span style={{ fontSize: 12, color: C.muted }}>{unit}</span>}
      </div>
    </div>
  );
}

function ResultTable({ result }) {
  if (!result) return null;
  const { title, columns, rows, insight, _not_found } = result;
  if (_not_found) return (
    <div style={{ padding: "20px", background: "#fffbeb",
      border: `1px solid #fcd34d`, borderRadius: 8, color: "#92400e", fontSize: 13 }}>
      ⚠ {insight}
    </div>
  );

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 8, overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", background: C.bg,
        borderBottom: `1px solid ${C.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{title}</span>
        <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10,
          background: "#dcfce7", color: C.home, fontWeight: 600 }}>⚡ Anlık</span>
      </div>
      {columns.length > 0 && rows.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: C.bg }}>
                {columns.map((col, i) => (
                  <th key={i} style={{
                    padding: "8px 16px", textAlign: i === 0 ? "left" : "right",
                    fontSize: 10, letterSpacing: 1, textTransform: "uppercase",
                    color: C.muted, borderBottom: `1px solid ${C.border}`,
                  }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} style={{ borderBottom: `1px solid ${C.border}` }}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{
                      padding: "10px 16px", textAlign: ci === 0 ? "left" : "right",
                      fontWeight: ci === 0 ? 600 : 400,
                      color: ci === 0 ? C.text : C.sub,
                    }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {insight && (
        <div style={{ padding: "10px 18px", background: "#fffbeb",
          borderTop: `1px solid ${C.border}`, fontSize: 13, color: "#92400e" }}>
          💡 {insight}
        </div>
      )}
    </div>
  );
}

// ── Ana bileşen ──────────────────────────────────────────────────────────────
export default function AnalyticsQuery({ leagues, byLeague, selectedLeague: externalLeague }) {
  const [league,    setLeague]    = useState(externalLeague || "");
  const [metric,    setMetric]    = useState("draw");
  const [location,  setLocation]  = useState("all");
  const [timeType,  setTimeType]  = useState("last_n");
  const [lastN,     setLastN]     = useState(10);
  const [topN,      setTopN]      = useState(5);
  const [order,     setOrder]     = useState("desc");
  const [goalMin,   setGoalMin]   = useState("");
  const [goalMax,   setGoalMax]   = useState("");
  const [useGoalMin, setUseGoalMin] = useState(false);
  const [useGoalMax, setUseGoalMax] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [result,    setResult]    = useState(null);
  const [history,   setHistory]   = useState([]);

  // Dış lig filtresi değişince iç state güncelle
  useEffect(() => {
    if (externalLeague !== undefined) {
      setLeague(externalLeague || "");
      setResult(null);
    }
  }, [externalLeague]);

  const isScores = metric === "scores";

  const handleQuery = useCallback(async () => {
    if (!league) return;
    setLoading(true);
    try {
      const payload = {
        league, metric, location,
        time_type: timeType, last_n: lastN, top_n: topN, order,
        goal_filter_min: useGoalMin && goalMin !== "" ? Number(goalMin) : null,
        goal_filter_max: useGoalMax && goalMax !== "" ? Number(goalMax) : null,
      };
      const resp = await fetch(`${RAILWAY_URL}/api/analytics/structured`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const res = await resp.json();
      const result = res.result;
      setResult(result);
      setHistory(h => [{ params: { ...payload }, result }, ...h].slice(0, 10));
    } catch (e) {
      setResult({ title: "Hata", columns: [], rows: [],
        insight: e.message, _not_found: true });
    }
    setLoading(false);
  }, [league, metric, location, timeType, lastN, topN, order, goalMin, goalMax, useGoalMin, useGoalMax]);

  const reset = () => { setResult(null); setHistory([]); };

  // Seçimlere göre otomatik açıklama cümlesi üret
  const buildSummary = () => {
    if (!league || !result) return null;
    const metricLabel = {
      draw:          "en çok berabere kalan",
      win:           "en çok galibiyet alan",
      lose:          "en çok mağlubiyet alan",
      goals_for:     "en çok gol atan",
      goals_against: "en çok gol yiyen",
      avg_goals:     "maç başı gol ortalaması en yüksek",
      zero_zero:     "en çok 0-0 biten maçı olan",
      scores:        "skor dağılımı",
    }[metric] || metric;
    const locationLabel = { all: "", home: " iç sahada", away: " deplasmanda" }[location] || "";
    const timeLabel = {
      last_n:      `son ${lastN} maçta`,
      this_season: "bu sezonda",
      last_season: "geçen sezonda",
      all:         "tüm zamanlarda",
    }[timeType] || "";
    const orderLabel = order === "desc" ? "en fazladan en aza" : "en azdan en fazlaya";
    if (metric === "scores") {
      return `${league}'de ${timeLabel}${locationLabel} görülen skor dağılımı`;
    }
    return `${league}'de ${timeLabel}${locationLabel} ${metricLabel} takımlar (${orderLabel})`;
  };

  return (
    <div>
      {/* ── Sorgu Paneli ── */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 8, padding: "24px", marginBottom: 20 }}>

        {/* Satır 1: Metrik + Lokasyon (Lig üstten geliyor) */}
        {league ? (
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>🏆 {league}</span>
            {byLeague?.[league] && (
              <span style={{ fontSize: 12, color: C.muted }}>
                — {byLeague[league].n?.toLocaleString("tr-TR")} maç verisi
              </span>
            )}
          </div>
        ) : (
          <div style={{ marginBottom: 16, padding: "10px 14px", background: "#fffbeb",
            border: "1px solid #fcd34d", borderRadius: 6, fontSize: 13, color: "#92400e" }}>
            ⚠ Üstteki filtreden bir lig seçin
          </div>
        )}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
          <Select label="Metrik" value={metric} onChange={v => { setMetric(v); setResult(null); }} options={METRICS} />
          {!isScores && <Select label="Lokasyon" value={location} onChange={v => { setLocation(v); setResult(null); }} options={LOCATIONS} />}
          {!isScores && <Select label="Sıralama" value={order} onChange={v => { setOrder(v); setResult(null); }} options={ORDERS} />}
        </div>

        {/* Satır 2: Zaman + Numerik girdiler */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20,
          padding: "16px", background: C.bg, borderRadius: 6, border: `1px solid ${C.border}` }}>
          <div style={{ minWidth: 200 }}>
            <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1,
              textTransform: "uppercase", marginBottom: 8 }}>Zaman Aralığı</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {TIME_TYPES.map(t => (
                <label key={t.value} style={{ display: "flex", alignItems: "center",
                  gap: 8, cursor: "pointer", fontSize: 13 }}>
                  <input type="radio" name="time_type" value={t.value}
                    checked={timeType === t.value}
                    onChange={() => { setTimeType(t.value); setResult(null); }}
                    style={{ accentColor: C.accent }} />
                  <span style={{ color: timeType === t.value ? C.text : C.sub,
                    fontWeight: timeType === t.value ? 600 : 400 }}>{t.label}</span>
                </label>
              ))}
            </div>
          </div>

          {timeType === "last_n" && (
            <div style={{ flex: 1, minWidth: 200 }}>
              <NumInput label="Son N Maç" value={lastN} onChange={v => { setLastN(v); setResult(null); }} min={1} max={100} unit="maç" />
            </div>
          )}

          {!isScores && (
            <div style={{ flex: 1, minWidth: 200 }}>
              <NumInput label="Sonuç Sayısı" value={topN} onChange={v => { setTopN(v); setResult(null); }} min={3} max={20} unit="takım" />
            </div>
          )}
        </div>

        {/* Satır 3: Gol filtresi (opsiyonel) */}
        <div style={{ padding: "14px 16px", background: C.bg, borderRadius: 6,
          border: `1px solid ${C.border}`, marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1,
            textTransform: "uppercase", marginBottom: 10 }}>
            Gol Filtresi (opsiyonel) — Maç bazlı toplam gol
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8,
              cursor: "pointer", fontSize: 13 }}>
              <input type="checkbox" checked={useGoalMin}
                onChange={e => { setUseGoalMin(e.target.checked); setResult(null); }}
                style={{ accentColor: C.accent }} />
              <span>En az</span>
              <input type="number" value={goalMin} min={0} max={20}
                disabled={!useGoalMin}
                onChange={e => { setGoalMin(e.target.value); setResult(null); }}
                style={{
                  width: 56, padding: "5px 8px", borderRadius: 5, textAlign: "center",
                  border: `1px solid ${useGoalMin ? C.accent : C.border}`,
                  fontSize: 13, fontWeight: 700,
                  background: useGoalMin ? C.card : "#f3f4f6",
                }} />
              <span>gol olan maçlar</span>
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: 8,
              cursor: "pointer", fontSize: 13 }}>
              <input type="checkbox" checked={useGoalMax}
                onChange={e => { setUseGoalMax(e.target.checked); setResult(null); }}
                style={{ accentColor: C.accent }} />
              <span>En fazla</span>
              <input type="number" value={goalMax} min={0} max={20}
                disabled={!useGoalMax}
                onChange={e => { setGoalMax(e.target.value); setResult(null); }}
                style={{
                  width: 56, padding: "5px 8px", borderRadius: 5, textAlign: "center",
                  border: `1px solid ${useGoalMax ? C.accent : C.border}`,
                  fontSize: 13, fontWeight: 700,
                  background: useGoalMax ? C.card : "#f3f4f6",
                }} />
              <span>gol olan maçlar</span>
            </label>
          </div>
        </div>

        {/* Butonlar */}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={handleQuery} disabled={!league || loading} style={{
            padding: "11px 28px", borderRadius: 6, border: "none",
            background: !league || loading ? C.muted : C.accent,
            color: "#fff", fontWeight: 700, fontSize: 14, cursor: league && !loading ? "pointer" : "not-allowed",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            {loading ? "⏳ Hesaplanıyor..." : "🔍 Analiz Et"}
          </button>
          {(result || history.length > 0) && (
            <button onClick={reset} style={{
              padding: "11px 16px", borderRadius: 6,
              border: `1px solid ${C.border}`, background: C.card,
              color: C.sub, fontSize: 13, cursor: "pointer",
            }}>🔄 Sıfırla</button>
          )}
        </div>
      </div>

      {/* ── Sonuç ── */}
      {result && (
        <div>
          {buildSummary() && (
            <div style={{
              padding: "10px 16px", marginBottom: 12,
              background: "#eff6ff", border: "1px solid #bfdbfe",
              borderRadius: 6, fontSize: 13, color: "#1d4ed8", fontWeight: 500,
            }}>
              📌 {buildSummary()}
            </div>
          )}
          <ResultTable result={result} />
        </div>
      )}

      {/* ── Geçmiş sorgular ── */}
      {history.length > 1 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12,
            textTransform: "uppercase", letterSpacing: 1 }}>
            Önceki Sorgular ({history.length - 1})
          </div>
          {history.slice(1).map((h, i) => (
            <div key={i} style={{ marginBottom: 16, opacity: 0.8 }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
                {METRICS.find(m => m.value === h.params.metric)?.label} ·{" "}
                {LOCATIONS.find(l => l.value === h.params.location)?.label} ·{" "}
                {TIME_TYPES.find(t => t.value === h.params.time_type)?.label}
                {h.params.time_type === "last_n" ? ` (${h.params.last_n} maç)` : ""}
              </div>
              <ResultTable result={h.result} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
